import React, { createContext, useState, useContext, useEffect, useLayoutEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const AuthProvider = ({ children }) => {
  // Restore the last known user optimistically so a cold PWA start (or offline)
  // shows the dashboard immediately instead of bouncing to login.
  const [user, setUser] = useState(() => {
    try { const u = localStorage.getItem('user'); return u ? JSON.parse(u) : null; } catch { return null; }
  });
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(!(localStorage.getItem('token') && localStorage.getItem('user')));

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchCurrentUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  // Global 401 handler with silent refresh-token rotation. On an expired/invalid
  // access token we transparently refresh and retry; only if refresh fails do we
  // clear the session and send the user to login. Persists until manual logout.
  // useLayoutEffect so the interceptor is registered BEFORE the [token] effect
  // fires the first /auth/me request (avoids a startup race that skipped refresh).
  useLayoutEffect(() => {
    let isRefreshing = false;
    let queue = [];

    const flushQueue = (error, newToken) => {
      queue.forEach(({ resolve, reject, cfg }) => {
        if (error) { reject(error); return; }
        cfg.headers = cfg.headers || {};
        cfg.headers.Authorization = `Bearer ${newToken}`;
        resolve(axios(cfg));
      });
      queue = [];
    };

    const forceLogout = () => {
      localStorage.removeItem('token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
      setToken(null);
      setUser(null);
      delete axios.defaults.headers.common['Authorization'];
      if (window.location.pathname !== '/') window.location.assign('/');
    };

    const id = axios.interceptors.response.use(
      (res) => res,
      async (error) => {
        const status = error?.response?.status;
        const cfg = error?.config || {};
        const url = cfg.url || '';
        const isAuthCall = url.includes('/auth/login') || url.includes('/auth/parent/login')
          || url.includes('/auth/register') || url.includes('/auth/refresh');
        const onParent = window.location.pathname.startsWith('/parent');

        if (status !== 401 || isAuthCall || onParent || cfg._retry) {
          return Promise.reject(error);
        }

        const refreshToken = localStorage.getItem('refresh_token');
        if (!refreshToken) { forceLogout(); return Promise.reject(error); }

        cfg._retry = true;
        if (isRefreshing) {
          return new Promise((resolve, reject) => queue.push({ resolve, reject, cfg }));
        }
        isRefreshing = true;
        try {
          const { data } = await axios.post(`${API_URL}/auth/refresh`, { refresh_token: refreshToken });
          localStorage.setItem('token', data.access_token);
          localStorage.setItem('refresh_token', data.refresh_token);
          axios.defaults.headers.common['Authorization'] = `Bearer ${data.access_token}`;
          flushQueue(null, data.access_token);
          cfg.headers = cfg.headers || {};
          cfg.headers.Authorization = `Bearer ${data.access_token}`;
          return axios(cfg);
        } catch (e) {
          flushQueue(e, null);
          forceLogout();
          return Promise.reject(e);
        } finally {
          isRefreshing = false;
        }
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/auth/me`);
      setUser(response.data);
      localStorage.setItem('user', JSON.stringify(response.data));
    } catch (error) {
      // A genuinely invalid session (401 that even a refresh can't fix) is handled by the
      // interceptor's forceLogout. A NETWORK error (no HTTP response — offline, slow cold
      // start on mobile PWA) must NOT log the user out: keep the cached session and let
      // later requests retry. This is what makes login survive app-close / no-internet.
      if (!error?.response) {
        console.warn('Session validation skipped (offline/network). Keeping cached session.');
      } else {
        console.error('Failed to fetch user:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API_URL}/auth/login`, { email, password });
      const { access_token, refresh_token, user: userData } = response.data;

      setToken(access_token);
      setUser(userData);
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(userData));
      if (refresh_token) localStorage.setItem('refresh_token', refresh_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Login failed'
      };
    }
  };

  const register = async (email, password, name, role) => {
    try {
      await axios.post(`${API_URL}/auth/register`, { email, password, name, role });
      return await login(email, password);
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Registration failed'
      };
    }
  };

  const logout = async () => {
    const refreshToken = localStorage.getItem('refresh_token');
    if (refreshToken) {
      try { await axios.post(`${API_URL}/auth/logout`, { refresh_token: refreshToken }); } catch (e) { /* ignore */ }
    }
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    delete axios.defaults.headers.common['Authorization'];
  };

  return (
    <AuthContext.Provider value={{ user, token, login, register, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export { API_URL };